/**
 * UpdateModal - PWA update notification modal
 * 
 * Shows when a new version of the app is available.
 * Allows user to update immediately or dismiss.
 */

import { useTranslation } from 'react-i18next';
import './UpdateModal.css';

interface UpdateModalProps {
    isOpen: boolean;
    onUpdate: () => void;
    onDismiss: () => void;
}

export default function UpdateModal({ isOpen, onUpdate, onDismiss }: UpdateModalProps) {
    const { t } = useTranslation();

    if (!isOpen) return null;

    return (
        <div className="update-modal-overlay" onClick={onDismiss}>
            <div className="update-modal" onClick={(e) => e.stopPropagation()}>
                <button className="update-close-btn" onClick={onDismiss} aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>

                <div className="update-icon">
                    <div className="update-icon-circle">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    </div>
                </div>

                <h3>{t('update.title', { defaultValue: 'New Version Available' })}</h3>
                <p>{t('update.description', { defaultValue: 'A new version of Raisa is available with improvements and new features.' })}</p>

                <div className="update-features">
                    <div className="update-feature">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                        </svg>
                        <span>{t('update.feature_performance', { defaultValue: 'Improved performance' })}</span>
                    </div>
                    <div className="update-feature">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span>{t('update.feature_security', { defaultValue: 'Security updates' })}</span>
                    </div>
                    <div className="update-feature">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                            <line x1="9" y1="9" x2="9.01" y2="9" />
                            <line x1="15" y1="9" x2="15.01" y2="9" />
                        </svg>
                        <span>{t('update.feature_experience', { defaultValue: 'Better experience' })}</span>
                    </div>
                </div>

                <button className="update-cta-btn" onClick={onUpdate}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                    </svg>
                    {t('update.button_update', { defaultValue: 'Update Now' })}
                </button>

                <button className="update-later-btn" onClick={onDismiss}>
                    {t('update.button_later', { defaultValue: 'Later' })}
                </button>
            </div>
        </div>
    );
}
